/**
 * Сводка эффективных правил поля шаблона (фаза 9 — первый шаг: таблица без сырого JSON там,
 * где структура известна: флаги, validation_rules, ACL, профиль).
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { CardTemplateFieldCreate } from "@/types/card-template";

interface CardTemplateFieldRulesSummaryProps {
  field: CardTemplateFieldCreate;
  /** Подпись профиля, если известна по справочнику */
  ruleProfileLabel?: string | null;
}

function formatJson(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function CardTemplateFieldRulesSummary({
  field,
  ruleProfileLabel,
}: CardTemplateFieldRulesSummaryProps) {
  const vr = field.validation_rules ?? {};
  const vrEntries =
    typeof vr === "object" && vr !== null && !Array.isArray(vr)
      ? Object.entries(vr as Record<string, unknown>)
      : [];

  const perm = field.permissions_config ?? {};
  const permEntries =
    typeof perm === "object" && perm !== null && !Array.isArray(perm)
      ? Object.entries(perm as Record<string, unknown>)
      : [];

  const vis = field.visibility_rules ?? {};
  const hasVis =
    typeof vis === "object" &&
    vis !== null &&
    !Array.isArray(vis) &&
    Object.keys(vis as object).length > 0;

  return (
    <div className="rounded-md border bg-muted/30 p-2 space-y-2">
      <p className="text-[11px] font-medium text-muted-foreground">Сводка правил (эффективные значения)</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px] h-8 text-xs">Параметр</TableHead>
            <TableHead className="h-8 text-xs">Значение</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="text-xs font-mono">is_required</TableCell>
            <TableCell className="text-xs">{field.is_required ? "да" : "нет"}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="text-xs font-mono">is_readonly</TableCell>
            <TableCell className="text-xs">{field.is_readonly ? "да" : "нет"}</TableCell>
          </TableRow>
          {field.applies_to === "geometry" && (
            <TableRow>
              <TableCell className="text-xs font-mono">inherit_from_object</TableCell>
              <TableCell className="text-xs">
                {field.inherit_from_object ? "да" : "нет"}
              </TableCell>
            </TableRow>
          )}
          {field.rule_profile_id != null && (
            <TableRow>
              <TableCell className="text-xs font-mono">rule_profile_id</TableCell>
              <TableCell className="text-xs">
                {field.rule_profile_id}
                {ruleProfileLabel ? ` (${ruleProfileLabel})` : ""}
              </TableCell>
            </TableRow>
          )}
          {vrEntries.map(([k, v]) => (
            <TableRow key={`vr-${k}`}>
              <TableCell className="text-xs font-mono">validation.{k}</TableCell>
              <TableCell className="text-xs break-all">{formatJson(v)}</TableCell>
            </TableRow>
          ))}
          {permEntries.map(([k, v]) => (
            <TableRow key={`perm-${k}`}>
              <TableCell className="text-xs font-mono">permissions.{k}</TableCell>
              <TableCell className="text-xs break-all">{formatJson(v)}</TableCell>
            </TableRow>
          ))}
          {hasVis && (
            <TableRow>
              <TableCell className="text-xs font-mono">visibility_rules</TableCell>
              <TableCell className="text-xs break-all">{formatJson(vis)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
